import { Divider, Stack, Tooltip } from '@mui/material';
import { MarkupOrPriceSuggestionCondition, RoundingCondition } from '@retail/calculus/types';
import { ConditionTooltipInfo, LevelChipWithChange } from '@retail/calculus/components';
import { getPriceSuggestionType } from '@retail/products/utils';
import { useAppTFunction } from '@retail/app/i18n';

interface Props {
  currentCondition: MarkupOrPriceSuggestionCondition;
  currentRoundingCondition?: RoundingCondition;
  futureCondition?: MarkupOrPriceSuggestionCondition;
  futureRoundingCondition?: RoundingCondition;
}

export function PriceTypeTooltip({
  currentCondition,
  currentRoundingCondition,
  futureCondition,
  futureRoundingCondition,
}: Props) {
  const t = useAppTFunction();

  const title =
    currentCondition.type === 'MARKUP'
      ? currentRoundingCondition
        ? t('myAssortment.originatesFrom.markupPlusRounding')
        : t('myAssortment.originatesFrom.markup')
      : currentRoundingCondition
        ? t('myAssortment.originatesFrom.priceSuggestionPlusRounding')
        : t('myAssortment.originatesFrom.priceSuggestion');

  const futureTitle =
    futureCondition?.type === 'MARKUP'
      ? futureRoundingCondition
        ? t('myAssortment.originatesFrom.futureMarkupPlusRounding')
        : t('myAssortment.originatesFrom.futureMarkup')
      : futureRoundingCondition
        ? t('myAssortment.originatesFrom.futurePriceSuggestionPlusRounding')
        : t('myAssortment.originatesFrom.futurePriceSuggestion');

  return (
    <Tooltip
      slotProps={{ tooltip: { sx: { minWidth: 'fit-content' } } }}
      title={
        <Stack gap={1}>
          <Stack flexDirection="row" gap={4}>
            <ConditionTooltipInfo
              title={title}
              bindingType={currentCondition.bindingType}
              bindingTarget={currentCondition.bindingTarget ?? ''}
              selectedPriceSuggestionType={
                currentCondition.priceSuggestionType &&
                getPriceSuggestionType(t, currentCondition.priceSuggestionType)
              }
              roundingCondition={
                currentRoundingCondition
                  ? {
                    bindingType: currentRoundingCondition.bindingType,
                    bindingTargetName: currentRoundingCondition.bindingTargetName,
                    roundingRuleName: currentRoundingCondition.roundingRule.name,
                  }
                  : undefined
              }
            />
            {futureCondition && (
              <>
                <Divider orientation="vertical" flexItem />
                <ConditionTooltipInfo
                  title={futureTitle}
                  bindingType={futureCondition.bindingType}
                  bindingTarget={futureCondition.bindingTarget}
                  selectedPriceSuggestionType={
                    futureCondition.priceSuggestionType &&
                    getPriceSuggestionType(t, futureCondition.priceSuggestionType)
                  }
                  roundingCondition={
                    futureRoundingCondition
                      ? {
                        bindingType: futureRoundingCondition.bindingType,
                        bindingTargetName: futureRoundingCondition.bindingTargetName,
                        roundingRuleName: futureRoundingCondition.roundingRule.name,
                      }
                      : undefined
                  }
                />
              </>
            )}
          </Stack>
        </Stack>
      }
    >
      <span>
        <LevelChipWithChange
          currentPriceSource={
            currentCondition.type === 'MARKUP'
              ? currentRoundingCondition
                ? 'markupPlusRounding'
                : 'markup'
              : currentRoundingCondition
                ? 'priceSuggestionPlusRounding'
                : 'priceSuggestion'
          }
          futurePriceSource={
            futureCondition
              ? futureCondition.type === 'MARKUP'
                ? futureRoundingCondition
                  ? 'markupPlusRounding'
                  : 'markup'
                : currentRoundingCondition
                  ? 'priceSuggestionPlusRounding'
                  : 'priceSuggestion'
              : undefined
          }
        />
      </span>
    </Tooltip>
  );
}
